import React from "react";
import HeroSequence from "@/components/blocks/HeroSequence";
import LogoLockup from "@/components/LogoLockup";
import { useLang } from "@/lib/i18n";

/**
 * Home hero — Ken-Burns crossfade slideshow of eight cinematic stills
 * spanning Iberia and Italy. Mix of architecture · sailboat · craft ·
 * gastronomy · art · vineyards so the sequence reads as a full editorial.
 * Headline + subtitle + silver tagline + scroll indicator overlay the slideshow.
 *
 * All eight frames are served from `/public/static/hero-sequence/` with paired
 * WebP variants for modern browsers. kenBurns rotates between zoom-in ·
 * pan-right · zoom-out · pan-left so no two consecutive slides share motion.
 */
const local = (name) => ({
  src: `/static/hero-sequence/${name}.jpg`,
  srcWebp: `/static/hero-sequence/${name}.webp`,
});

const HERO_SLIDES = [
  {
    ...local("alhambra-golden-hour"),
    subject: "The Alhambra at golden hour with snow-capped Sierra Nevada behind, surrounded by cypress",
    kenBurns: "zoom-in",
    overlayStrength: "heavy",
  },
  {
    ...local("sailboat-mallorca"),
    subject: "A wooden sailboat resting on glassy Mediterranean water off the Mallorcan coast",
    kenBurns: "pan-right",
    overlayStrength: "medium",
  },
  {
    ...local("craft-pottery"),
    subject: "Hands of a Spanish ceramic master shaping clay on the wheel in a sunlit workshop",
    kenBurns: "zoom-out",
    overlayStrength: "heavy",
  },
  {
    ...local("sintra-pena-palace"),
    subject: "Sintra's Pena Palace rising above a lush green hillside in morning mist",
    kenBurns: "pan-left",
    overlayStrength: "medium",
  },
  {
    ...local("gastronomy-candlelit"),
    subject: "A candlelit table set with wine, bread and the suggestion of an Iberian dinner",
    kenBurns: "zoom-in",
    overlayStrength: "heavy",
  },
  {
    ...local("douro-valley"),
    subject: "Aerial of the Douro Valley near Pinhão — terraced vineyards and the river curving below",
    kenBurns: "pan-right",
    overlayStrength: "light",
  },
  {
    ...local("art-renaissance"),
    subject: "A Renaissance fresco glimpsed through the marble columns of an Italian palazzo",
    kenBurns: "zoom-out",
    overlayStrength: "medium",
  },
  {
    ...local("ronda-blue-hour"),
    subject: "Ronda's Puente Nuevo illuminated at blue hour above the El Tajo gorge",
    kenBurns: "pan-left",
    overlayStrength: "light",
  },
];

export default function Hero() {
  const { t } = useLang();
  return (
    <section
      data-testid="home-hero"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-charcoal text-ivory"
    >
      <HeroSequence
        slides={HERO_SLIDES}
        durationPerSlide={4700}
        fadeDuration={1200}
      />

      <div
        className="absolute inset-0 pointer-events-none z-10"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,15,15,0.10) 0%, rgba(15,15,15,0.0) 30%, rgba(15,15,15,0.0) 55%, rgba(15,15,15,0.42) 100%)",
        }}
        aria-hidden
      />

      <div className="relative z-20 h-full">
        <div className="mx-auto max-w-7xl h-full px-6 md:px-10 flex flex-col justify-end pb-28 md:pb-36">
          <div className="max-w-3xl hero-legible">
            <p
              className="eyebrow text-brass mb-6 animate-rise-in"
              style={{ animationDelay: "200ms", opacity: 0 }}
              data-testid="hero-eyebrow"
            >
              {t.home.eyebrow}
            </p>
            <h1
              className="font-display text-ivory font-normal leading-[1.02] tracking-tight animate-rise-in text-balance"
              style={{ fontSize: "clamp(2.75rem, 7vw, 6rem)", animationDelay: "350ms", opacity: 0 }}
              data-testid="hero-headline"
            >
              {t.home.headline_main}
              <br />
              <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>
                {t.home.headline_italic}
              </span>
            </h1>
            <p
              className="italic-quote text-ivory/85 mt-6 animate-rise-in text-balance"
              style={{ fontSize: "clamp(1.15rem, 1.7vw, 1.5rem)", maxWidth: "38ch", lineHeight: 1.4, animationDelay: "550ms", opacity: 0 }}
              data-testid="hero-subtitle"
            >
              {t.home.subtitle}
            </p>
            <div
              className="mt-7 animate-rise-in"
              style={{ animationDelay: "750ms", opacity: 0 }}
              data-testid="hero-tagline-wrap"
            >
              <LogoLockup variant="full" size="xl" tone="dark" />
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 animate-rise-in"
          aria-hidden
          data-testid="hero-scroll-indicator"
          style={{ animationDelay: "700ms", opacity: 0 }}
        >
          <span className="eyebrow text-ivory/80 hero-legible">{t.home.scroll}</span>
          <span className="block w-px h-12 bg-ivory/60 origin-top animate-scroll-pulse" />
        </div>
      </div>
    </section>
  );
}
